import { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { pushSupported, requestPushToken, revokePushToken } from "../lib/push";
import { useAuth } from "./useAuth";
import { useCouple } from "./useCouple";
import type { NotificationCategory, NotificationPrefs } from "../types";

export function useNotifications() {
  const { user } = useAuth();
  const { profile } = useCouple();
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    pushSupported().then(setSupported);
  }, []);

  // A token on the profile means THIS browser/device enabled push before —
  // not perfectly precise (another device's token would also show here
  // since we only keep one per account), but good enough for a 2-person app
  // and avoids the complexity of a per-device token subcollection.
  const enabled = !!profile?.fcmToken;
  const prefs: NotificationPrefs = profile?.notificationPrefs ?? {};

  async function enable() {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const token = await requestPushToken();
      if (!token) {
        setPermission(Notification.permission);
        setError(
          Notification.permission === "denied"
            ? "Benachrichtigungen sind blockiert. Das lässt sich nur in den Browser-/Handy-Einstellungen ändern."
            : "Benachrichtigungen konnten nicht aktiviert werden."
        );
        return;
      }
      await updateDoc(doc(db, "users", user.uid), { fcmToken: token });
      setPermission("granted");
    } catch {
      setError("Benachrichtigungen konnten nicht aktiviert werden.");
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!user) return;
    setBusy(true);
    try {
      await revokePushToken();
      await updateDoc(doc(db, "users", user.uid), { fcmToken: null });
    } finally {
      setBusy(false);
    }
  }

  async function setPref(category: NotificationCategory, value: boolean) {
    if (!user) return;
    await updateDoc(doc(db, "users", user.uid), { [`notificationPrefs.${category}`]: value });
  }

  return { supported, permission, enabled, prefs, busy, error, enable, disable, setPref };
}
